import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, Button, TextField, Alert, Divider, CircularProgress, Chip } from '@mui/material';
import DrawIcon from '@mui/icons-material/Draw';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArticleIcon from '@mui/icons-material/Article';
import api from '../services/api';

export default function SignContract() {
  const token = new URLSearchParams(window.location.search).get('token');
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);
  const [error, setError] = useState('');
  const [signedName, setSignedName] = useState('');
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!token) { setError('Invalid signing link'); setLoading(false); return; }
    api.get(`/contracts/sign/${token}`)
      .then(res => { setContract(res.data); setLoading(false); })
      .catch(() => { setError('Invalid or expired signing link'); setLoading(false); });
  }, [token]);

  const handleSign = async () => {
    if (!signedName || !agreed) return;
    setSubmitting(true);
    try {
      await api.put(`/contracts/sign/${token}`, { signed_name: signedName });
      setSigned(true);
    } catch (err) {
      setError('Failed to sign. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      <Card sx={{ borderRadius: 3, p: 4, maxWidth: 400, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ color: '#e53935', mb: 2 }}>Invalid Link</Typography>
        <Typography sx={{ color: '#666' }}>{error}</Typography>
      </Card>
    </Box>
  );

  if (signed) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      <Card sx={{ borderRadius: 3, p: 6, maxWidth: 500, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 80, color: '#2e7d32', mb: 2 }} />
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e', mb: 1 }}>Contract Signed!</Typography>
        <Typography sx={{ color: '#666', mb: 1 }}>Thank you, <strong>{signedName}</strong>.</Typography>
        <Typography variant="body2" sx={{ color: '#999' }}>
          The contract for <strong>{contract?.project_name}</strong> is now fully executed. 
          A Purchase Order has been automatically generated.
        </Typography>
      </Card>
    </Box>
  );

  if (contract?.subcontractor_signed_at) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      <Card sx={{ borderRadius: 3, p: 4, maxWidth: 400, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: '#2e7d32', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e' }}>Already Signed</Typography>
        <Typography sx={{ color: '#666', mt: 1 }}>This contract has already been signed by {contract.subcontractor_signed_name}.</Typography>
      </Card>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #1a237e, #283593)', py: 3, px: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <ArticleIcon sx={{ color: 'white', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" sx={{ color: 'white', fontWeight: 800 }}>Yaplar</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>Contract Signature Request</Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 600, mx: 'auto', py: 4, px: 2 }}>
        <Card elevation={0} sx={{ borderRadius: 3, mb: 3, p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e', mb: 2 }}>Contract Details</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[
              { label: 'Project', value: contract?.project_name },
              { label: 'Owner', value: `${contract?.first_name || ''} ${contract?.last_name || ''}`.trim() },
              { label: 'Contract Amount', value: `$${Number(contract?.contract_amount || 0).toLocaleString()}` },
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f5f6fa' }}>
                <Typography variant="body2" sx={{ color: '#999' }}>{item.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.value}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Chip label="Owner Signed ✓" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
            <Chip label="Your Signature Pending" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
          </Box>
        </Card>

        {contract?.contract_file_url && (
          <Card elevation={0} sx={{ borderRadius: 3, mb: 3, p: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', mb: 1.5 }}>Contract Document</Typography>
            <Button variant="outlined" href={contract.contract_file_url} target="_blank"
              startIcon={<VisibilityIcon />} sx={{ borderRadius: 2 }}>
              Review Contract PDF
            </Button>
          </Card>
        )}

        <Card elevation={0} sx={{ borderRadius: 3, p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', mb: 2 }}>Your Signature</Typography>
          <TextField fullWidth label="Type your full legal name *" value={signedName}
            onChange={e => setSignedName(e.target.value)}
            placeholder="e.g. Bob Smith"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, fontStyle: 'italic', fontSize: 18 } }} />
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, bgcolor: '#f5f6fa', borderRadius: 2, mb: 3 }}>
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
              style={{ marginTop: 3, cursor: 'pointer', width: 16, height: 16 }} />
            <Typography variant="body2" sx={{ color: '#666' }}>
              By typing my name and checking this box, I agree this constitutes my legal electronic signature 
              and I agree to all terms and conditions of this contract.
            </Typography>
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          <Button fullWidth variant="contained" onClick={handleSign}
            disabled={submitting || !signedName || !agreed}
            startIcon={<DrawIcon />}
            sx={{ bgcolor: '#2e7d32', borderRadius: 2, py: 1.5, fontSize: 16, fontWeight: 700 }}>
            {submitting ? 'Signing...' : 'Sign Contract'}
          </Button>
        </Card>
      </Box>
    </Box>
  );
}
